import { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import NiceModal from '@ebay/nice-modal-react';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import { endOfDay, isAfter } from 'date-fns';
import { useSnackbar } from 'notistack';
import { includes } from 'ramda';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import Form from '@gef-ui/form-material/Form';
import { getLabelMessage } from '@gef-ui/localization/messages';

import { useDefaultRedirect } from 'app/hooks';
import Panel from 'components/atoms/GefMui/Panel';
import ProspectorButton from 'components/atoms/GefMui/ProspectorButton';
import Subheader from 'components/atoms/GefMui/Subheader';
import { ComponentLoader } from 'components/index';
import MODALS from 'constants/modals';
import PAGES from 'constants/pages';
import fetch from 'features/common/fetch';
import { urls } from 'features/common/fetch/constants';
import { useRenderSubheaderTitle } from 'features/common/Subheader/hooks';
import {
	fetchLeadHistory,
	openLead,
	resetLeadDetail,
	returnLeadToMarketPlace,
} from 'features/views/leadDetail/actions';
import { USER_HAS_NO_ACCESS_TO_LEAD_ERR_MSG } from 'features/views/leadDetail/constants';
import formDescriptorFn, { LEAD_DETAIL_FORM } from 'features/views/leadDetail/form';
import { getLeadData, getLeadHistory } from 'features/views/leadDetail/selectors';
import LeadTaskHistoryModal from 'pages/LeadTaskHistoryModal/modals/LeadTaskHistoryModal';

import LeadDetailFeedback from './components/LeadDetailFeedback';
import LeadInfo from './components/LeadInfo';
import Notes from './components/Notes';

export const LeadDetail = ({ leadId }) => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { enqueueSnackbar } = useSnackbar();
	const defaultRedirectIfFetchFails = useDefaultRedirect();
	const { state } = useLocation();
	const locationFrom = state?.from;

	const leadData = useSelector(getLeadData);
	const leadHistory = useSelector(getLeadHistory);

	useEffect(() => {
		dispatch(openLead(leadId));
	}, [dispatch, leadId]);

	useEffect(
		() => () => {
			dispatch(resetLeadDetail());
		},
		[dispatch],
	);

	const { status: errorStatus = null, parsedContent = {} } = useSelector(fetch.selectors.getMetaData(urls.leadOpen));

	useEffect(() => {
		if (errorStatus === 403 && includes(USER_HAS_NO_ACCESS_TO_LEAD_ERR_MSG, parsedContent?.message)) {
			if (locationFrom) {
				// marketPlace, dashboardSeller
				navigate(-1);
			} else {
				// direct url to detail (from GPK etc)
				navigate(defaultRedirectIfFetchFails);
			}
			enqueueSnackbar(getLabelMessage('toastr:leadDetailErrorUserNotResolver'), { variant: 'error' });
			dispatch(fetch.actions.clearFetchStore(urls.leadOpen));
		}
	}, [
		navigate,
		enqueueSnackbar,
		dispatch,
		errorStatus,
		parsedContent?.message,
		locationFrom,
		defaultRedirectIfFetchFails,
	]);

	const subheaderTitle = useRenderSubheaderTitle({
		pageTitle: `${getLabelMessage('leadDetail:leadInfo')} - ${leadData?.taskTypeName}`,
		backButton: true,
	});

	const handleReloadLeadHistory = useCallback(() => {
		dispatch(fetchLeadHistory(leadData?.id));
	}, [dispatch, leadData?.id]);

	const handleReturnLeadToMarketPlace = useCallback(() => {
		dispatch(returnLeadToMarketPlace(leadData?.id));
	}, [dispatch, leadData?.id]);

	const handleHistoryButton = useCallback(() => {
		NiceModal.show(MODALS.lead_task_history, {
			historyItems: leadHistory,
			onReloadFn: handleReloadLeadHistory,
		});
	}, [leadHistory, handleReloadLeadHistory]);

	const { isFromMarket, statusId, deadlineDate, solverIsLoggedUser } = leadData;
	const today = new Date();
	const isLeadNotEnded = isAfter(endOfDay(new Date(deadlineDate)), today);
	const showReturnToMarketPlaceButton = isFromMarket && statusId !== 3 && isLeadNotEnded && solverIsLoggedUser;

	const subheaderToolbar = (
		<Stack
			direction="row"
			spacing={1}
			sx={{
				justifyContent: 'flex-end',
			}}
		>
			<ProspectorButton businessIn={leadData?.client?.businessIn} />
			{showReturnToMarketPlaceButton && (
				<ComponentLoader fetchStorePath={urls.returnLead} ignoreErrors>
					<Button variant="text" size="small" onClick={handleReturnLeadToMarketPlace}>
						{getLabelMessage('leadDetail:returnLeadToMarketPlace')}
					</Button>
				</ComponentLoader>
			)}
			<Button variant="text" size="small" onClick={handleHistoryButton}>
				{getLabelMessage('leadDetail:leadHistory')}
			</Button>
		</Stack>
	);

	return (
		<Panel screenId={PAGES.leadDetail.id} screenName={PAGES.leadDetail.name}>
			<LeadTaskHistoryModal id={MODALS.lead_task_history} />
			<Paper sx={{ p: 3, borderRadius: 2 }}>
				<Stack spacing={2}>
					<Subheader title={subheaderTitle} headerToolbar={subheaderToolbar} />
					<Stack spacing={4}>
						<LeadDetailFeedback lead={leadData} />
						<Form name={LEAD_DETAIL_FORM} formDescriptorFn={formDescriptorFn}>
							<Notes leadId={leadData?.id} comments={leadData?.comments} />
						</Form>
					</Stack>
				</Stack>
			</Paper>
			<LeadInfo lead={leadData} />
		</Panel>
	);
};

LeadDetail.propTypes = {
	leadId: PropTypes.string,
};

export default LeadDetail;
